/**
 * generateRandomSet.js
 *
 * Generates a full set of tennis events with realistic ball positions.
 *
 * Coordinate system (0–100 normalized):
 *   x : 0 = left edge, 100 = right edge
 *   y : 0 = top baseline, 50 = net, 100 = bottom baseline
 *
 * currentSide:
 *   0 = top player hitting (y ≈ 2–8)
 *   1 = bottom player hitting (y ≈ 92–98)
 *
 * Serve zone naming follows CodingCourt.jsx conventions:
 *   deuceServeBottom — target when top player (side=0) serves deuce  : x:50-87, y:50-77
 *   adServeBottom    — target when top player (side=0) serves ad     : x:13-50, y:50-77
 *   deuceServeTop    — target when bottom player (side=1) serves deuce: x:13-50, y:23-50
 *   adServeTop       — target when bottom player (side=1) serves ad  : x:50-87, y:23-50
 */

import {
    kBackhand,
    kForehand,
    kRally,
    kRallyDrive,
    kRallyDropshot,
    kRallyOutcomeForcingError,
    kRallyOutcomeIn,
    kRallyOutcomeUnforcedError,
    kRallyOutcomeWinner,
    kRallySlice,
    kRallyTopspin,
    kRallyVolley,
    kReturnIn,
    kReturning,
    kReturnUnforcedError,
    kReturnWinner,
    kServeAce,
    kServeFirst,
    kServeIn,
    kServeOut,
    kServeSecond,
    kServing,
    kStageFirstServeDeuce,
} from './pstsDefines';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const rnd = (lo, hi) => lo + Math.random() * (hi - lo);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const choose = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ─── Court geometry constants (0-100 normalized) ──────────────────────────

const TRAM_L = 13;
const TRAM_R = 87;
const SVC_LINE_T = 23; // service line y in top half
const SVC_LINE_B = 77; // service line y in bottom half

// Service box landing zones (valid in-zone serve landings)
// Top player (currentSide=0) serves downward (y increases)
const ZONE_DEUCE_BOT = { x1: 51,         y1: 51,           x2: TRAM_R - 1, y2: SVC_LINE_B - 1 };
const ZONE_AD_BOT    = { x1: TRAM_L + 1, y1: 51,           x2: 49,         y2: SVC_LINE_B - 1 };
// Bottom player (currentSide=1) serves upward (y decreases)
const ZONE_DEUCE_TOP = { x1: TRAM_L + 1, y1: SVC_LINE_T + 1, x2: 49,         y2: 49 };
const ZONE_AD_TOP    = { x1: 51,         y1: SVC_LINE_T + 1, x2: TRAM_R - 1, y2: 49 };

// Rally landing zones
const ZONE_RALLY_BOT = { x1: TRAM_L + 1, y1: 51, x2: TRAM_R - 1, y2: 97 };
const ZONE_RALLY_TOP = { x1: TRAM_L + 1, y1: 3,  x2: TRAM_R - 1, y2: 49 };

// Distance thresholds (1 normalized unit ≈ 23.77 cm on the length axis)
const NET_Y       = 50;
const DEEP_LO     = 8.4;   // 200 cm from baseline
const DEEP_HI     = 12.6;  // 300 cm from baseline
const NET_LO      = 4.2;   // 100 cm from net  → drop-shot lower bound
const NET_HI      = 8.4;   // 200 cm from net  → drop-shot upper bound
const VOLLEY_ZONE = 12.6;  // 300 cm from net  → volley start threshold

function randInZone(z) {
  return { x: rnd(z.x1, z.x2), y: rnd(z.y1, z.y2) };
}

// ─── Position helpers ─────────────────────────────────────────────────────

/**
 * Where the server stands before serving — close to the centre mark,
 * right on the baseline.
 *
 * Deuce serve: server stands to their RIGHT of the centre mark (looking at net).
 *   Top player faces DOWN  → their right = screen LEFT  (x < 50) → deuce x < 50
 *   Bottom player faces UP → their right = screen RIGHT (x > 50) → deuce x > 50
 * Ad serve: opposite sides.
 *
 * y is placed on the baseline:
 *   Top player    y ≈ −1 to 1   (on the y=0 baseline)
 *   Bottom player y ≈ 99 to 101  (on the y=100 baseline)
 */
function serveStartPos(currentSide, isDeuce) {
  if (currentSide === 0) {
    // top player faces down — deuce = their right = screen left (x < 50)
    return { x: isDeuce ? rnd(41, 49) : rnd(51, 59), y: rnd(-1, 1) };
  } else {
    // bottom player faces up — deuce = their right = screen right (x > 50)
    return { x: isDeuce ? rnd(51, 59) : rnd(41, 49), y: rnd(99, 101) };
  }
}

/**
 * Compute where the next player contacts the ball.
 *
 * The ball travels from `shotStart` (where the previous player hit it) to
 * `shotEnd` (where it lands / end-position of that event).  After the bounce
 * the ball continues in the same direction — the next player intercepts it
 * somewhere along that extension.  We model the carry-past-landing as a
 * fixed fraction (30–55 %) of the original shot length, with a small random
 * lateral jitter so the player isn't always perfectly on the line.
 *
 * hitterIsTop=true  → top player hits: y clamped to 3–22
 * hitterIsTop=false → bottom player hits: y clamped to 78–97
 */
function hitPosition(shotStart, shotEnd, hitterIsTop) {
  const dx = shotEnd.x - shotStart.x;
  const dy = shotEnd.y - shotStart.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;

  // Extend the trajectory beyond the landing point
  const carry = len * rnd(0.30, 0.55);
  const ux = dx / len;
  const uy = dy / len;

  // Small lateral jitter (perpendicular component)
  const jitter = rnd(-4, 4);
  let hx = shotEnd.x + ux * carry + (-uy) * jitter;
  let hy = shotEnd.y + uy * carry + ux * jitter;

  if (hitterIsTop) {
    hy = clamp(hy, 3, 45);   // allow net-approach into service-box depth
  } else {
    hy = clamp(hy, 55, 97);  // allow net-approach
  }
  hx = clamp(hx, TRAM_L + 1, TRAM_R - 1);
  return { x: hx, y: hy };
}

function randomShotType() {
  return choose([kRallyTopspin, kRallyTopspin, kRallyTopspin, kRallySlice, kRallyDrive]);
}

function randomHand() {
  return Math.random() < 0.62 ? kForehand : kBackhand;
}

function rallyLandZone(hitterIsTop) {
  return hitterIsTop ? ZONE_RALLY_BOT : ZONE_RALLY_TOP;
}

/**
 * In-play landing for a return or rally shot.
 *   60 % land deep — 200–300 cm from the far baseline.
 *    5 % land near the net — 100–200 cm from net (drop-shot territory).
 *   35 % land anywhere else in the zone.
 */
function deepRallyLand(hitterIsTop) {
  const r = Math.random();
  if (hitterIsTop) {
    // Shooting to bottom half; far baseline at y=100
    if (r < 0.60) return { x: rnd(TRAM_L + 1, TRAM_R - 1), y: rnd(100 - DEEP_HI, 100 - DEEP_LO) };
    if (r < 0.65) return { x: rnd(TRAM_L + 1, TRAM_R - 1), y: rnd(NET_Y + NET_LO, NET_Y + NET_HI) };
    return           { x: rnd(TRAM_L + 1, TRAM_R - 1), y: rnd(51, 97) };
  } else {
    // Shooting to top half; far baseline at y=0
    if (r < 0.60) return { x: rnd(TRAM_L + 1, TRAM_R - 1), y: rnd(DEEP_LO, DEEP_HI) };
    if (r < 0.65) return { x: rnd(TRAM_L + 1, TRAM_R - 1), y: rnd(NET_Y - NET_HI, NET_Y - NET_LO) };
    return           { x: rnd(TRAM_L + 1, TRAM_R - 1), y: rnd(3, 49) };
  }
}

/**
 * Classify shot subskill from hitter and landing positions.
 *   Volley    : hitter within 300 cm of net  (|hitPt.y − 50| ≤ 12.6)
 *   Drop shot : lands 100–200 cm from net    (|landPt.y − 50| ∈ [4.2, 8.4])
 *   Otherwise : random drive / slice / topspin
 */
function shotSubskill(hitPt, landPt) {
  if (Math.abs(hitPt.y - NET_Y) <= VOLLEY_ZONE) return kRallyVolley;
  const d = Math.abs(landPt.y - NET_Y);
  if (d >= NET_LO && d <= NET_HI) return kRallyDropshot;
  return randomShotType();
}

/**
 * Landing for an IN serve.
 * 90% land within 10–20 cm of the service line (0.5–2 normalized units inside it).
 * 10% land anywhere in the service box.
 */
function inServeLand(serverIsTop, isDeuce) {
  const nearLine = Math.random() < 0.90;
  const inset = nearLine ? rnd(0.5, 2.0) : null;
  if (serverIsTop) {
    // target box is in the bottom half; service line is at SVC_LINE_B
    const boxX = isDeuce
      ? { lo: 51, hi: TRAM_R - 1 }
      : { lo: TRAM_L + 1, hi: 49 };
    return {
      x: rnd(boxX.lo, boxX.hi),
      y: nearLine ? SVC_LINE_B - inset : rnd(51, SVC_LINE_B - 0.5),
    };
  } else {
    // target box is in the top half; service line is at SVC_LINE_T
    const boxX = isDeuce
      ? { lo: TRAM_L + 1, hi: 49 }
      : { lo: 51, hi: TRAM_R - 1 };
    return {
      x: rnd(boxX.lo, boxX.hi),
      y: nearLine ? SVC_LINE_T + inset : rnd(SVC_LINE_T + 0.5, 49),
    };
  }
}

// Court scale: 100 units = 23.77 m length  →  1 unit ≈ 24 cm
//              74 units  =  8.23 m width   →  1 unit ≈ 11 cm
// 10–20 cm translates to ≈ 0.5–2.0 normalized units on either axis.
const JUST_OUT = () => rnd(0.5, 2.0);

/**
 * Landing for a FAULT serve — ball lands just outside the service box.
 *   60% long  : just past the service line (SVC_LINE_T / SVC_LINE_B)
 *   30% wide  : just outside a singles sideline
 *   10% net   : drops near the net cord on the server's side
 */
function justOutServeLand(serverIsTop, isDeuce) {
  const which = Math.random();
  const out = JUST_OUT();
  if (serverIsTop) {
    // target box is in the bottom half
    // deuce → right box (x:51-86) → wide = past right sideline (x > TRAM_R)
    // ad    → left box  (x:14-49) → wide = past left sideline  (x < TRAM_L)
    const boxX = isDeuce
      ? { lo: 51, hi: TRAM_R - 1 }
      : { lo: TRAM_L + 1, hi: 49 };
    if (which < 0.60) {
      // long — just past the service line
      return { x: rnd(boxX.lo, boxX.hi), y: SVC_LINE_B + out };
    } else if (which < 0.90) {
      // wide — only past the outer sideline of the target box
      return isDeuce
        ? { x: TRAM_R + out, y: rnd(51, SVC_LINE_B) }
        : { x: TRAM_L - out, y: rnd(51, SVC_LINE_B) };
    } else {
      // net — drops on the server's side near the net
      return { x: rnd(boxX.lo, boxX.hi), y: rnd(47, 50) };
    }
  } else {
    // target box is in the top half
    // deuce → left box  (x:14-49) → wide = past left sideline  (x < TRAM_L)
    // ad    → right box (x:51-86) → wide = past right sideline (x > TRAM_R)
    const boxX = isDeuce
      ? { lo: TRAM_L + 1, hi: 49 }
      : { lo: 51, hi: TRAM_R - 1 };
    if (which < 0.60) {
      return { x: rnd(boxX.lo, boxX.hi), y: SVC_LINE_T - out };
    } else if (which < 0.90) {
      return isDeuce
        ? { x: TRAM_L - out, y: rnd(SVC_LINE_T, 49) }
        : { x: TRAM_R + out, y: rnd(SVC_LINE_T, 49) };
    } else {
      return { x: rnd(boxX.lo, boxX.hi), y: rnd(50, 53) };
    }
  }
}

/**
 * Landing for an error rally / return shot — ball lands just outside the
 * baseline or sideline.
 *   50% long  : 0.5–2 units past the far baseline
 *   35% wide  : 0.5–2 units past a singles sideline
 *   15% net   : drops near the net (inside the court)
 */
function justOutRallyLand(hitterIsTop) {
  const which = Math.random();
  const out = JUST_OUT();
  if (hitterIsTop) {
    // hitter at top shoots toward bottom half
    if (which < 0.50) {
      return { x: rnd(TRAM_L, TRAM_R), y: 100 + out };
    } else if (which < 0.85) {
      return Math.random() < 0.5
        ? { x: TRAM_L - out, y: rnd(52, 99) }
        : { x: TRAM_R + out, y: rnd(52, 99) };
    } else {
      return { x: rnd(TRAM_L, TRAM_R), y: rnd(49, 51) };
    }
  } else {
    // hitter at bottom shoots toward top half
    if (which < 0.50) {
      return { x: rnd(TRAM_L, TRAM_R), y: -out };
    } else if (which < 0.85) {
      return Math.random() < 0.5
        ? { x: TRAM_L - out, y: rnd(1, 48) }
        : { x: TRAM_R + out, y: rnd(1, 48) };
    } else {
      return { x: rnd(TRAM_L, TRAM_R), y: rnd(49, 51) };
    }
  }
}

// ─── Point simulation ─────────────────────────────────────────────────────

/**
 * Simulate one point and return all its events plus who won.
 *
 * @param {object} params
 * @param {number} params.currentSide  0=top player serves, 1=bottom player serves
 * @param {boolean} params.isDeuce     true = deuce side, false = ad side
 * @param {object} params.server       player object for the server
 * @param {object} params.returner     player object for the returner
 * @param {object} params.scoreSnapshot scores at start of point
 * @param {number} params.timestamp    seconds timestamp for first event
 * @returns {{ events: Array, serverWon: boolean }}
 */
function simulatePoint({ currentSide, isDeuce, server, returner, scoreSnapshot, timestamp }) {
  const events = [];
  const serverIsTop = currentSide === 0;
  let t = timestamp;

  // ── First serve ──────────────────────────────────────────────────────────
  const sStart = serveStartPos(currentSide, isDeuce);

  const firstIn = Math.random() < 0.62;
  let serveLand;

  if (firstIn) {
    serveLand = inServeLand(serverIsTop, isDeuce);
    events.push({
      skill: kServing, subskill1: kServeFirst, subskill2: null,
      hand: kForehand, outcome: kServeIn, result: 0,
      start: { ...sStart }, end: { ...serveLand },
      player: server, scores: { ...scoreSnapshot }, reach: 0, timestampseconds: t,
    });
    t += rnd(2, 6);
  } else {
    // First serve fault — ball lands just outside the service box
    const faultLand = justOutServeLand(serverIsTop, isDeuce);
    events.push({
      skill: kServing, subskill1: kServeFirst, subskill2: null,
      hand: kForehand, outcome: kServeOut, result: -1,
      start: { ...sStart }, end: { ...faultLand },
      player: server, scores: { ...scoreSnapshot }, reach: 0, timestampseconds: t,
    });
    t += rnd(3, 8);

    // ── Second serve ──────────────────────────────────────────────────────
    const s2Start = serveStartPos(currentSide, isDeuce);
    const secondIn = Math.random() < 0.84;

    if (secondIn) {
      serveLand = inServeLand(serverIsTop, isDeuce);
      events.push({
        skill: kServing, subskill1: kServeSecond, subskill2: null,
        hand: kForehand, outcome: kServeIn, result: 0,
        start: { ...s2Start }, end: { ...serveLand },
        player: server, scores: { ...scoreSnapshot }, reach: 0, timestampseconds: t,
      });
      t += rnd(2, 6);
    } else {
      // Double fault — ball lands just outside the service box
      const dfLand = justOutServeLand(serverIsTop, isDeuce);
      events.push({
        skill: kServing, subskill1: kServeSecond, subskill2: null,
        hand: kForehand, outcome: kServeOut, result: -1,
        start: { ...s2Start }, end: { ...dfLand },
        player: server, scores: { ...scoreSnapshot }, reach: 0, timestampseconds: t,
      });
      return { events, serverWon: false };
    }
  }

  // Ace / serve winner (~8%)
  if (Math.random() < 0.08) {
    const lastEv = events[events.length - 1];
    lastEv.result = 1;
    lastEv.outcome = kServeAce;
    return { events, serverWon: true };
  }

  // ── Return ────────────────────────────────────────────────────────────────
  const returnerIsTop = !serverIsTop;
  const retStart = hitPosition(sStart, serveLand, returnerIsTop);

  // Return error (~18%)
  if (Math.random() < 0.18) {
    const isRetWinner = Math.random() < 0.18;
    const retLand = isRetWinner
      ? randInZone(rallyLandZone(returnerIsTop))
      : justOutRallyLand(returnerIsTop);
    events.push({
      skill: kReturning, subskill1: 0, subskill2: randomShotType(),
      hand: randomHand(),
      outcome: isRetWinner ? kReturnWinner : kReturnUnforcedError,
      result: isRetWinner ? 1 : -1,
      start: { ...retStart }, end: { ...retLand },
      player: returner, scores: { ...scoreSnapshot },
      reach: Math.random() < 0.30 ? 1 : 0, timestampseconds: t,
    });
    return { events, serverWon: !isRetWinner };
  }

  // Return in play
  const retLand = deepRallyLand(returnerIsTop);
  events.push({
    skill: kReturning, subskill1: 0, subskill2: shotSubskill(retStart, retLand),
    hand: randomHand(), outcome: kReturnIn, result: 0,
    start: { ...retStart }, end: { ...retLand },
    player: returner, scores: { ...scoreSnapshot },
    reach: Math.random() < 0.25 ? 1 : 0, timestampseconds: t,
  });
  t += rnd(1.5, 4.5);

  // ── Rally ─────────────────────────────────────────────────────────────────
  // Server hits the first rally ball (after returner's return).
  // prevShotStart / prevShotEnd are the start/end of the last event, used to
  // extend the trajectory and place the next hitter realistically.
  let prevShotStart = retStart;
  let prevShotEnd = retLand;
  let rallyHitterIsTop = serverIsTop;
  let rallyHitter = server;

  for (let shot = 0; shot < 50; shot++) {
    // Next hitter's position = extension of prevShotStart → prevShotEnd trajectory
    const hitPt = hitPosition(prevShotStart, prevShotEnd, rallyHitterIsTop);

    // Error probability grows linearly with rally length
    const errProb = Math.min(0.28 + shot * 0.045, 0.75);

    if (Math.random() < errProb) {
      // Terminal shot: winner or error
      const isWinner = Math.random() < 0.28;
      const termLand = isWinner
        ? randInZone(rallyLandZone(rallyHitterIsTop))
        : justOutRallyLand(rallyHitterIsTop);
      const outcome = isWinner
        ? kRallyOutcomeWinner
        : Math.random() < 0.60
          ? kRallyOutcomeUnforcedError
          : kRallyOutcomeForcingError;

      events.push({
        skill: kRally, subskill1: 0, subskill2: randomShotType(),
        hand: randomHand(), outcome, result: isWinner ? 1 : -1,
        start: { ...hitPt }, end: { ...termLand },
        player: rallyHitter, scores: { ...scoreSnapshot },
        reach: Math.random() < 0.22 ? 1 : 0, timestampseconds: t,
      });

      const serverWon = isWinner
        ? rallyHitter.guid === server.guid
        : rallyHitter.guid !== server.guid;
      return { events, serverWon };
    }

    // In-play rally shot
    const landPt = deepRallyLand(rallyHitterIsTop);
    events.push({
      skill: kRally, subskill1: 0, subskill2: shotSubskill(hitPt, landPt),
      hand: randomHand(), outcome: kRallyOutcomeIn, result: 0,
      start: { ...hitPt }, end: { ...landPt },
      player: rallyHitter, scores: { ...scoreSnapshot },
      reach: Math.random() < 0.18 ? 1 : 0, timestampseconds: t,
    });
    t += rnd(1.5, 4.0);

    prevShotStart = hitPt;
    prevShotEnd = landPt;
    rallyHitterIsTop = !rallyHitterIsTop;
    rallyHitter = rallyHitter.guid === server.guid ? returner : server;
  }

  // Safety fallback (should not normally be reached)
  return { events, serverWon: Math.random() < 0.5 };
}

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * Generate all events for a complete set of tennis.
 *
 * @param {object} params
 * @param {object} params.player1         player1 object (with .guid)
 * @param {object} params.player2         player2 object
 * @param {object} params.currentServer   which player is currently serving
 * @param {object} params.topPlayer       player currently at the top of screen
 * @param {object} params.bottomPlayer    player currently at the bottom
 * @param {object} params.scores          current { player1SetScore, player2SetScore, ... }
 *
 * @returns {{ events: Array, finalState: object }}
 */
export function generateRandomSet({ player1, player2, currentServer, topPlayer, bottomPlayer, scores: initScores }) {
  const allEvents = [];
  let p1Set = initScores.player1SetScore;
  let p2Set = initScores.player2SetScore;
  let server = currentServer;
  let topP = topPlayer;
  let bottomP = bottomPlayer;
  let ts = Date.now() / 1000;

  const isTop = (p) => p.guid === topP.guid;

  const getSetWinner = () => {
    if (p1Set >= 6 && p1Set >= p2Set + 2) return 1;
    if (p2Set >= 6 && p2Set >= p1Set + 2) return 2;
    if (p1Set === 7) return 1;
    if (p2Set === 7) return 2;
    return 0;
  };

  const getGameWinner = (p1g, p2g) => {
    if (p1g >= 4 && p1g >= p2g + 2) return 1;
    if (p2g >= 4 && p2g >= p1g + 2) return 2;
    return 0;
  };

  let safety = 0;
  while (getSetWinner() === 0 && safety++ < 50) {
    let p1g = 0;
    let p2g = 0;
    let gamePointNum = 0;

    while (getGameWinner(p1g, p2g) === 0) {
      const serverIsTop = isTop(server);
      const currentSide = serverIsTop ? 0 : 1;
      // Even game-point index → deuce side; odd → ad side
      const isDeuce = gamePointNum % 2 === 0;
      const returner = server.guid === player1.guid ? player2 : player1;

      const scoreSnapshot = {
        player1GameScore: p1g,
        player2GameScore: p2g,
        player1SetScore: p1Set,
        player2SetScore: p2Set,
      };

      const { events: ptEvts, serverWon } = simulatePoint({
        currentSide,
        isDeuce,
        server,
        returner,
        scoreSnapshot,
        timestamp: ts,
      });

      allEvents.push(...ptEvts);
      ts += rnd(20, 50);

      if (serverWon) {
        server.guid === player1.guid ? p1g++ : p2g++;
      } else {
        server.guid === player1.guid ? p2g++ : p1g++;
      }
      gamePointNum++;
    }

    // Update set score
    const gw = getGameWinner(p1g, p2g);
    gw === 1 ? p1Set++ : p2Set++;

    // Tiebreak at 6-6: skip detailed simulation, just pick a winner
    if (p1Set === 6 && p2Set === 6) {
      Math.random() < 0.5 ? p1Set++ : p2Set++;
      break;
    }

    // Switch server after each game
    server = server.guid === player1.guid ? player2 : player1;

    // Switch ends after every odd total game count
    const totalGames = p1Set + p2Set;
    if (totalGames % 2 === 1) {
      const newTop = topP.guid === player1.guid ? player2 : player1;
      topP = newTop;
      bottomP = topP.guid === player1.guid ? player2 : player1;
    }
  }

  const finalState = {
    scores: {
      player1GameScore: 0,
      player2GameScore: 0,
      player1SetScore: p1Set,
      player2SetScore: p2Set,
    },
    currentPlayer: server,
    currentServer: server,
    currentStage: kStageFirstServeDeuce,
    currentSide: isTop(server) ? 0 : 1,
    topPlayer: topP,
    bottomPlayer: bottomP,
    pointNumber: 0,
    event: allEvents[allEvents.length - 1] || null,
    events: allEvents,
  };

  return { events: allEvents, finalState };
}
